import React from "react";
import { CourseExamApi, courseExamsApi } from "api/course-exams";
import { ConfirmDeleteButton } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { formatDate } from "utils";
import { useToaster } from "utils";
import { PaziaBox } from "components/atoms";
import { ExamPaymentPoliciesPreview } from "./ExamPaymentPoliciesPreview/ExamPaymentPoliciesPreview";
import { htmlParser } from "utils/htmlParser";
import { useCourseSessionsOptions, usePlacesOptions } from "api/hooks";
import { IDropdownOption } from "components/atoms";

interface ExamsPreviewProps {
  loading: boolean;
  exams: CourseExamApi[];
  courseId: number;
  onSuccessDelete: () => void;
  onEdit: (exam: CourseExamApi) => void;
}
export const ExamsPreview = ({ loading, exams, onEdit, courseId, onSuccessDelete }: ExamsPreviewProps) => {
  const { options: sessionsOptions } = useCourseSessionsOptions(courseId);
  const { options: placesOptions } = usePlacesOptions();

  if (loading) return <PaziaLoader position="center" />;

  return (
    <div className="flex flex-col gap-4">
      {exams.length === 0 && <PaziaBox>Brak egzaminów</PaziaBox>}
      {exams.map((exam) => (
        <ExamRow
          key={exam.id}
          exam={exam}
          courseId={courseId}
          onSuccessDelete={onSuccessDelete}
          onEdit={onEdit}
          sessionsOptions={sessionsOptions}
          placesOptions={placesOptions}
        />
      ))}
    </div>
  );
};

interface ExamRowProps {
  exam: CourseExamApi;
  courseId: number;
  onSuccessDelete: () => void;
  onEdit: (exam: CourseExamApi) => void;
  sessionsOptions: IDropdownOption[];
  placesOptions: IDropdownOption[];
}
const ExamRow = ({ exam, courseId, onEdit, onSuccessDelete, sessionsOptions, placesOptions }: ExamRowProps) => {
  const { showInfo } = useToaster();
  const { name, description, id, payments } = exam;

  const onDelete = async () => {
    try {
      await courseExamsApi.delete(courseId, id);
      showInfo("Egzamin został usunięty.");
      onSuccessDelete();
    } catch (e) {
      console.error(e);
      showInfo("Nie udało się usunąć egzaminu.");
    }
  };

  const sessionLabel = sessionsOptions.find((x) => x.value === exam.courseSessionId?.toString())?.label;
  const placeLabel = placesOptions.find((x) => x.value === exam.placeId?.toString())?.label;

  return (
    <PaziaBox>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between font-semibold text-slate-800 dark:text-slate-100">
          {name}
          <div className="flex gap-2">
            <ConfirmDeleteButton
              dialog={{
                title: "Usuń egzamin",
                description: "Czy na pewno chcesz usunąć ten egzamin?",
              }}
              title="Usuń"
              onConfirm={onDelete}
            />
            <PaziaButton type="tertiary" title="Edytuj" onClick={() => onEdit(exam)} />
          </div>
        </div>
        <div className="text-md font-semibold">
          {exam.day ? (
            <>
              {formatDate(exam.day)}, {exam.timeFrom}-{exam.timeTo}
              {sessionLabel ? ` · ${sessionLabel}` : ""}
              {placeLabel ? ` · ${placeLabel}` : ""}
            </>
          ) : (
            "Termin nieustalony"
          )}
        </div>
        {exam.trainers.length > 0 && (
          <div className="text-sm text-slate-500">
            Trenerzy: {exam.trainers.map((t) => `${t.firstName} ${t.surname}`).join(", ")}
          </div>
        )}
        {description && <div className="text-sm text-slate-500">{htmlParser.toHTML(description)}</div>}
        <ExamPaymentPoliciesPreview payments={payments} />
      </div>
    </PaziaBox>
  );
};
